import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoCard from '@/components/feature/VideoCard';
import { categories, allVideos, faqList, features, stats } from '@/mocks/videos';

function FAQAccordion({ q, a, isOpen, onToggle }: { q: string; a: string; isOpen: boolean; onToggle: () => void }) {
  return (
    <div className="border-b border-background-200">
      <button
        className="w-full py-5 flex items-center justify-between gap-4 text-left cursor-pointer"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <span className="text-sm md:text-base font-medium text-foreground-900">{q}</span>
        {isOpen ? (
          <i className="ri-arrow-up-s-line text-lg text-foreground-500 transition-transform duration-300 flex-shrink-0"></i>
        ) : (
          <i className="ri-arrow-down-s-line text-lg text-foreground-500 transition-transform duration-300 flex-shrink-0"></i>
        )}
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${
          isOpen ? 'max-h-48 pb-5' : 'max-h-0'
        }`}
      >
        <p className="text-sm text-foreground-600 leading-relaxed">{a}</p>
      </div>
    </div>
  );
}

export default function Home() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const featuredVideos = allVideos.slice(0, 6);
  const latestVideos = allVideos.slice(0, 8);

  const websiteJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '一起草在线观看',
    url: 'https://wgecino.cn',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://wgecino.cn/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const webpageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '一起草在线观看 - 免费高清视频播放平台',
    description: '一起草在线观看，提供海量高清视频资源，无需下载即可流畅播放。支持多终端访问，画质清晰，加载极速。',
    url: 'https://wgecino.cn',
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqList.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(websiteJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(webpageJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Dark%20cinematic%20abstract%20background%20with%20soft%20warm%20gradient%20lighting%20and%20subtle%20particle%20effects%2C%20modern%20streaming%20platform%20atmosphere%2C%20deep%20amber%20and%20coral%20tones%20with%20gentle%20glow%2C%20professional%20broadcast%20aesthetic%20without%20text%2C%20atmospheric%20and%20immersive%20visual%20texture&width=1920&height=1080&seq=hero-bg-home-2026&orientation=landscape"
              alt="一起草在线观看 - 免费高清视频播放平台"
              title="一起草在线观看"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
            <div className="relative z-10 text-center px-4 w-full max-w-3xl mx-auto">
              <h1 className="text-2xl md:text-5xl font-bold text-white font-heading mb-4 leading-tight">
                一起草在线观看
              </h1>
              <p className="text-sm md:text-lg text-white/80 mb-8 max-w-xl mx-auto leading-relaxed">
                海量高清视频资源，无需下载即可流畅播放。支持多终端访问，画质清晰，加载极速，随时随地畅享优质视频内容。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/category/dianshi"
                  className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white px-8 py-3 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-play-circle-line text-lg"></i>
                  立即观看
                </Link>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 border border-white/40 hover:border-white text-white/90 hover:text-white px-8 py-3 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  了解更多
                </Link>
              </div>
              <div className="flex items-center justify-center gap-6 mt-10">
                {stats.map((stat, i) => (
                  <div key={i} className="text-center">
                    <div className="text-lg md:text-2xl font-bold text-white font-heading">{stat.value}</div>
                    <div className="text-xs text-white/60 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Features Section */}
          <section className="py-14 md:py-20 px-4 md:px-6 lg:px-12 bg-background-50">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {features.map((feat, i) => (
                  <div key={i} className="text-center p-6 rounded-lg bg-background-100/70 hover:bg-background-100 transition-colors duration-300">
                    <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                      <i className={`${feat.icon} text-xl text-primary-500`}></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-2">{feat.title}</h3>
                    <p className="text-xs text-foreground-500">{feat.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Categories Section */}
          <section className="py-14 md:py-20 px-4 md:px-6 lg:px-12 bg-background-100/50">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
                  视频分类
                </h2>
                <p className="text-sm text-foreground-500 max-w-lg mx-auto">
                  涵盖电视剧、电影、综艺、动漫、纪录片、音乐等丰富类型，满足您的多样化观看需求
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="group flex flex-col items-center p-5 rounded-lg bg-background-50 hover:bg-primary-50 transition-all duration-300 cursor-pointer"
                  >
                    <div className="w-14 h-14 rounded-2xl bg-accent-100 flex items-center justify-center mb-3 group-hover:bg-accent-200 transition-colors duration-300">
                      <i className={`${cat.icon} text-2xl text-accent-600`}></i>
                    </div>
                    <span className="text-sm font-medium text-foreground-800 mb-1 whitespace-nowrap">{cat.name}</span>
                    <span className="text-xs text-foreground-400">{cat.count.toLocaleString()}部</span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Featured Videos Section */}
          <section className="py-14 md:py-20 px-4 md:px-6 lg:px-12 bg-background-50">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center justify-between mb-10">
                <div>
                  <h2 className="text-xl md:text-3xl font-bold text-foreground-950 font-heading mb-2">
                    精选推荐
                  </h2>
                  <p className="text-sm text-foreground-500">编辑团队为您精心挑选的优质内容</p>
                </div>
                <Link
                  to="/category/dianying"
                  className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors duration-300 cursor-pointer whitespace-nowrap"
                >
                  查看更多 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {featuredVideos.map((video) => (
                  <VideoCard key={video.id} {...video} />
                ))}
              </div>
            </div>
          </section>

          {/* Latest Videos Section */}
          <section className="py-14 md:py-20 px-4 md:px-6 lg:px-12 bg-background-100/50">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center justify-between mb-10">
                <div>
                  <h2 className="text-xl md:text-3xl font-bold text-foreground-950 font-heading mb-2">
                    最新上线
                  </h2>
                  <p className="text-sm text-foreground-500">实时更新，不错过任何精彩内容</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {latestVideos.map((video) => (
                  <VideoCard key={video.id} {...video} />
                ))}
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-14 md:py-20 px-4 md:px-6 lg:px-12 bg-background-50">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
                  常见问题
                </h2>
                <p className="text-sm text-foreground-500">关于一起草在线观看，您可能想了解的问题</p>
              </div>
              <div className="bg-background-100/70 rounded-lg px-6">
                {faqList.map((item, index) => (
                  <FAQAccordion
                    key={index}
                    q={item.q}
                    a={item.a}
                    isOpen={openFaq === index}
                    onToggle={() => setOpenFaq(openFaq === index ? null : index)}
                  />
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="relative py-20 md:py-28 px-4 md:px-6 lg:px-12 overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20abstract%20gradient%20background%20with%20flowing%20organic%20shapes%20and%20soft%20light%20beams%2C%20modern%20digital%20entertainment%20aesthetic%2C%20coral%20and%20amber%20color%20palette%2C%20smooth%20elegant%20texture%2C%20no%20text%2C%20atmospheric%20and%20inviting&width=1920&height=800&seq=cta-bg-home-2026&orientation=landscape"
              alt=""
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-foreground-950/70"></div>
            <div className="relative z-10 text-center max-w-xl mx-auto">
              <h2 className="text-xl md:text-3xl font-bold text-white font-heading mb-4">
                即刻开启您的观影之旅
              </h2>
              <p className="text-sm md:text-base text-white/70 mb-8">
                海量高清视频，无需注册，永久免费。一起草在线观看，随时随地畅享精彩内容。
              </p>
              <Link
                to="/category/dianshi"
                className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-white px-10 py-3.5 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-play-circle-line text-lg"></i>
                免费观影
              </Link>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}